import React from "react";
import styled from "styled-components";

import { redactLoreTerms } from "src/shared/utils/loreReveal";
import {
  LumaColorImg,
  LumaMutedImg,
  LumaStarActiveImg,
  LumaStarMutedImg,
} from "src/static/img";

type LumaCounterProps = {
  now: Date;
  progress: number;
};

const MILESTONES = [100, 75, 50, 25];

const LumaCounter: React.FC<LumaCounterProps> = ({ now, progress }) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 100);
  const displayedLumaName = redactLoreTerms("Luma", now);
  const desktopExplanation = redactLoreTerms(
    "Every project submitted, workshop attended, and activity completed restores luma back to the realm. Help push the counter to 100% before the event ends to overcome the Fade!",
    now
  );
  const mobileExplanation = redactLoreTerms(
    "A real-time measure of our collective energy. Every project submitted, workshop attended, and activity completed restores luma back to the realm. Help push the counter to 100% before the event ends to overcome the Fade!",
    now
  );

  return (
    <CounterContainer>
      <CounterTitle>Global {displayedLumaName} Counter</CounterTitle>
      <LumaVisual
        role="img"
        aria-label={`Global ${displayedLumaName} is ${Math.round(
          clampedProgress
        )}% restored`}
      >
        <MilestoneScale aria-hidden="true">
          {MILESTONES.map((milestone) => {
            const isReached = clampedProgress >= milestone;

            return (
              <Milestone key={milestone} isReached={isReached}>
                <span>{milestone}%</span>
                <MilestoneStar>
                  <img
                    src={isReached ? LumaStarActiveImg : LumaStarMutedImg}
                    alt=""
                  />
                </MilestoneStar>
              </Milestone>
            );
          })}
        </MilestoneScale>
        <Artwork>
          <ArtworkImage src={LumaMutedImg} alt="" aria-hidden="true" />
          <ColorReveal style={{ height: `${clampedProgress}%` }}>
            <ColorArtworkImage src={LumaColorImg} alt="" aria-hidden="true" />
          </ColorReveal>
        </Artwork>
      </LumaVisual>
      <Explanation>
        <DesktopExplanation>{desktopExplanation}</DesktopExplanation>
        <MobileExplanation>{mobileExplanation}</MobileExplanation>
      </Explanation>
    </CounterContainer>
  );
};

export default LumaCounter;

const CounterContainer = styled.section`
  display: flex;
  flex: 0 1 355px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  min-width: 300px;
  max-width: 355px;

  ${({ theme }) => theme.mediaQueries.medium`
    flex: none;
    width: 100%;
    max-width: 420px;
    align-self: center;
  `}

  ${({ theme }) => theme.mediaQueries.tablet`
    min-width: 0;
  `}
`;

const CounterTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.8px;
  text-align: center;
  text-transform: uppercase;
`;

const LumaVisual = styled.div`
  position: relative;
  width: 100%;
  max-width: 318.501px;
  height: 252px;
`;

const MilestoneScale = styled.div`
  position: absolute;
  top: 7px;
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 82.29px;
`;

const Milestone = styled.div<{ isReached: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  height: 35.29px;
  color: ${({ isReached }) => (isReached ? "#fcd34d" : "#dfdfdf")};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
`;

const MilestoneStar = styled.span`
  position: relative;
  width: 35.29px;
  height: 35.29px;
  flex: 0 0 35.29px;

  img {
    position: absolute;
    top: 6.86px;
    left: 7.23px;
    display: block;
    width: 20.823px;
    height: 20.107px;
  }
`;

const Artwork = styled.div`
  position: absolute;
  top: 14.9422px;
  right: 31.273px;
  width: 189.381651px;
  height: 203.214497px;
`;

const ArtworkImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
`;

const ColorReveal = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  transition: height 500ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ColorArtworkImage = styled(ArtworkImage)`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 189.381651px;
  height: 203.214497px;
  max-width: none;
`;

const Explanation = styled.div`
  width: 100%;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
`;

const DesktopExplanation = styled.p`
  margin: 0;

  ${({ theme }) => theme.mediaQueries.tablet`
    display: none;
  `}
`;

const MobileExplanation = styled.p`
  display: none;
  margin: 0;

  ${({ theme }) => theme.mediaQueries.tablet`
    display: block;
  `}
`;
